import axios from "axios";
import { useEffect } from "react";
import { useNavigate } from "react-router-dom";

const baseURL =
    process.env.NODE_ENV === "production"
        ? "/api"
        : "http://localhost:5000/api";

export const api = axios.create({
    baseURL,
    headers: { "Content-Type": "application/json" },
    withCredentials: true
});

let isRefreshing = false;
let failedQueue = [];

const processQueue = (error, token = null) => {
  failedQueue.forEach(prom => {
    if (error) { prom.reject(error); }
    else { prom.resolve(token); }
  });
  failedQueue = [];
};

export const useAxiosInterceptor = () => {
    const navigate = useNavigate();

    useEffect(() => {
        const requestInterceptor = api.interceptors.request.use((config) => {
            const token = localStorage.getItem("token");
            if (token) {
                config.headers.Authorization = `Bearer ${token}`;
            }
            return config;
        }, (error) => Promise.reject(error));

        const responseInterceptor = api.interceptors.response.use(
            (response) => response,
            async (error) => {
                const originalRequest = error.config;

                if (error.response?.status === 401 && !originalRequest._retry) {
                    // Never attempt refresh for the refresh endpoint itself
                    if (originalRequest.url === '/auth/refresh') {
                        localStorage.removeItem("token");
                        navigate("/signin");
                        return Promise.reject(error);
                    }

                    if (isRefreshing) {
                        return new Promise((resolve, reject) => {
                            failedQueue.push({ resolve, reject });
                        }).then(token => {
                            originalRequest.headers.Authorization = `Bearer ${token}`;
                            return api(originalRequest);
                        }).catch(err => Promise.reject(err));
                    }

                    originalRequest._retry = true;
                    isRefreshing = true;

                    try {
                        const { data } = await api.post('/auth/refresh', {}, { withCredentials: true });
                        localStorage.setItem("token", data.token);
                        api.defaults.headers.common.Authorization = `Bearer ${data.token}`;
                        processQueue(null, data.token);
                        originalRequest.headers.Authorization = `Bearer ${data.token}`;
                        return api(originalRequest);
                    } catch (refreshError) {
                        processQueue(refreshError, null);
                        localStorage.removeItem("token");
                        if (window.location.pathname !== "/signin") {
                            navigate("/signin");
                        }
                        return Promise.reject(refreshError);
                    } finally {
                        isRefreshing = false;
                    }
                }

                return Promise.reject(error);
            }
        );

        return () => {
            api.interceptors.request.eject(requestInterceptor);
            api.interceptors.response.eject(responseInterceptor);
        };
    }, [navigate]);
};
